import { DOCUMENT } from '@angular/common';
import type { HttpRequest } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { APP_CONFIG } from '@core/config/app-config.tokens';

export interface HttpHeaderContributorContext {
  readonly skipSessionAuth: boolean;
}

export type HttpHeaderMap = Readonly<Record<string, string>>;

export interface HttpHeaderContributor {
  contribute(
    request: HttpRequest<unknown>,
    _context: HttpHeaderContributorContext,
  ): HttpHeaderMap | null;
}

export const HTTP_HEADER_CONTRIBUTORS = new InjectionToken<readonly HttpHeaderContributor[]>(
  'HTTP_HEADER_CONTRIBUTORS',
  {
    factory: () => [],
  },
);

@Injectable({ providedIn: 'root' })
export class AxelorSessionHeaderContributor implements HttpHeaderContributor {
  private readonly appConfig = inject(APP_CONFIG);
  private readonly document = inject(DOCUMENT);

  public contribute(
    request: HttpRequest<unknown>,
    _context: HttpHeaderContributorContext,
  ): HttpHeaderMap | null {
    if (!isApiRequestUrl(request.url, this.appConfig.apiBaseUrl, this.document.baseURI)) {
      return null;
    }

    const headers: Record<string, string> = {};

    if (!request.headers.has('Accept')) {
      headers['Accept'] = 'application/json';
    }

    return Object.keys(headers).length > 0 ? headers : null;
  }
}

export function isApiRequestUrl(requestUrl: string, apiBaseUrl: string, baseUri: string): boolean {
  const apiUrl = new URL(apiBaseUrl, baseUri);
  const targetUrl = new URL(requestUrl, baseUri);

  if (apiUrl.origin !== targetUrl.origin) {
    return false;
  }

  const normalizedApiPath = normalizePathname(apiUrl.pathname);
  const normalizedTargetPath = normalizePathname(targetUrl.pathname);

  return (
    normalizedTargetPath === normalizedApiPath ||
    normalizedTargetPath.startsWith(`${normalizedApiPath}/`)
  );
}

function normalizePathname(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, '');

  return normalized.length > 0 ? normalized : '/';
}
